/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import type { Interpolation } from '@emotion/react';

import * as React from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';
import { MOMENT_FORMATS, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import { Button, Checkbox, Divider, Grid, NotificationBadge, TemporaryDrawer, Typography2 } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';
import { ChevronNextIcon } from '@coursera/cds-icons';

import type { DateRangeFilterOption, ProgramList } from 'bundles/enterprise-admin-drawers/components/types';

import _t from 'i18n!nls/enterprise-admin-drawers';

const styles: Record<string, Interpolation<Theme>> = {
  buttonWrapper: () => css`
    margin-right: var(--cds-spacing-200);
  `,
  buttonsWrapper: () => css`
    margin-top: var(--cds-spacing-200);
  `,
  labelText: () => css`
    margin-left: var(--cds-spacing-200);
    margin-bottom: var(--cds-spacing-200);
  `,
  divider: () => css`
    margin: var(--cds-spacing-100) 0;
  `,
  extraAction: () => css`
    margin-bottom: var(--cds-spacing-100);
  `,
};

type Props = {
  title: string;
  selectedPrograms: ProgramList;
  dateRange?: DateRangeFilterOption | null;
  oneCsvPerProgram: boolean;
  includeDeletedMembers: boolean;
  handleProgramViewSelection: () => void;
  handleDateRangeViewSelection: () => void;
  onExport: () => void;
  onIncludeDeletedMembersChange: () => void;
  onOneCSVPerProgramChange: () => void;
  onClose: () => void;
  extraSelectFilterViewComponent?: React.ReactNode;
  extraSelectFilterViewAction?: React.ReactNode;
};

const ExportDrawerSelectionView = ({
  title,
  selectedPrograms,
  dateRange,
  oneCsvPerProgram,
  includeDeletedMembers,
  extraSelectFilterViewComponent,
  extraSelectFilterViewAction,
  handleProgramViewSelection,
  handleDateRangeViewSelection,
  onIncludeDeletedMembersChange,
  onOneCSVPerProgramChange,
  onClose,
  onExport,
}: Props) => {
  const { HeadingGroup, Content, Actions } = TemporaryDrawer;

  return (
    <TemporaryDrawer
      open={true}
      anchor="right"
      onClose={onClose}
      sticky="actions"
      data-testid="export-drawer-selection-view"
    >
      <HeadingGroup>{title}</HeadingGroup>
      <Content>
        {extraSelectFilterViewComponent}
        <Button variant="ghost" fullWidth css={{ justifyContent: 'flex-start' }} onClick={handleProgramViewSelection}>
          <Grid container direction="row" justifyContent="space-between" data-testid="program-selection-button">
            <NotificationBadge
              value={selectedPrograms.length}
              max={999}
              align="inline"
              data-testid="program-amount-notification-badge"
            >
              {_t('Programs')}
            </NotificationBadge>
            <ChevronNextIcon color="interactive" />
          </Grid>
        </Button>
        {!selectedPrograms.length && (
          <Typography2
            component="p"
            variant="bodySecondary"
            color="supportText"
            css={styles.labelText}
            data-testid="all-programs-selected-text"
          >
            {_t('All programs')}
          </Typography2>
        )}
        <Divider css={styles.divider} />
        <Button
          variant="ghost"
          fullWidth
          css={{ justifyContent: 'flex-start' }}
          onClick={handleDateRangeViewSelection}
          data-testid="data-selection-view-button"
        >
          <Grid container direction="row" justifyContent="space-between">
            {_t('Date range')}
            <ChevronNextIcon color="interactive" />
          </Grid>
        </Button>
        <Typography2
          component="p"
          variant="bodySecondary"
          color="supportText"
          css={styles.labelText}
          data-testid="selected-date-range"
        >
          {dateRange?.endDate && dateRange?.startDate ? (
            <FormattedMessage
              message={_t('{start}-{end}')}
              start={formatDateTimeDisplay(dateRange.startDate, MOMENT_FORMATS.US_LOCAL_DATE)}
              end={formatDateTimeDisplay(dateRange.endDate, MOMENT_FORMATS.US_LOCAL_DATE)}
            />
          ) : (
            _t('All time')
          )}
        </Typography2>
      </Content>
      <Actions>
        <div css={styles.extraAction}>{extraSelectFilterViewAction}</div>
        <Grid container direction="column">
          <Checkbox
            checked={oneCsvPerProgram}
            onChange={onOneCSVPerProgramChange}
            label={_t('Export one CSV file per program')}
          />
          <Checkbox
            checked={includeDeletedMembers}
            onChange={onIncludeDeletedMembersChange}
            label={_t('Include learners removed from the program')}
          />
        </Grid>
        <Grid container direction="row" css={styles.buttonsWrapper}>
          <Button
            variant="secondary"
            css={styles.buttonWrapper}
            onClick={onClose}
            data-testid="export-drawer-cancel-button"
          >
            {_t('Cancel')}
          </Button>
          <Button variant="primary" onClick={onExport} data-testid="export-report-drawer-button">
            {_t('Export')}
          </Button>
        </Grid>
      </Actions>
    </TemporaryDrawer>
  );
};

export default ExportDrawerSelectionView;
